import React, { useState, useEffect } from 'react';
import {
  Activity,
  Inbox,
  Trash2,
  ChevronDown,
  ChevronUp,
  User,
  Calendar,
  Mail,
  AlertCircle,
} from 'lucide-react';
import { useSurveyStore } from '../store/useSurveyStore';
import type { SurveyField, SurveyResponse } from '../types';

interface Props {
  surveyId: string;
  onAnalyze?: (responseId: string) => void;
}

export default function SurveyResponses({ surveyId, onAnalyze }: Props) {
  const survey = useSurveyStore((s) => s.getSurveyById(surveyId));
  const responses = useSurveyStore((s) => s.getResponsesBySurvey(surveyId));
  const deleteResponse = useSurveyStore((s) => s.deleteResponse);
  const fetchResponsesAPI = useSurveyStore((s) => s.fetchResponsesAPI);

  useEffect(() => {
    if (surveyId) fetchResponsesAPI(surveyId);
  }, [surveyId, fetchResponsesAPI]);

  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (!survey) {
    return (
      <div className="text-center py-12">
        <AlertCircle size={32} className="mx-auto text-slate-400 mb-2" />
        <p className="text-sm text-slate-500">Ankieta nie znaleziona.</p>
      </div>
    );
  }

  const toggle = (id: string) => setExpandedId((prev) => (prev === id ? null : id));

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('pl-PL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const fieldLabelMap = new Map(survey.fields.map((f) => [f.id, f]));

  const renderAnswer = (field: SurveyField | undefined, value: unknown) => {
    if (value === undefined || value === null || value === '') return <span className="text-slate-400 italic">brak odpowiedzi</span>;

    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="text-slate-400 italic">brak wyboru</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((v, i) => (
            <span key={i} className="px-2 py-0.5 rounded bg-brand-100 dark:bg-brand-950 text-brand-700 dark:text-brand-300 text-xs font-medium">
              {String(v)}
            </span>
          ))}
        </div>
      );
    }

    if (typeof value === 'boolean') {
      return <span className={value ? 'text-green-600' : 'text-red-500'}>{value ? 'Tak' : 'Nie'}</span>;
    }

    return <span>{String(value)}</span>;
  };

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-brand-100 dark:bg-brand-950 flex items-center justify-center">
          <Inbox size={22} className="text-brand-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold">Odpowiedzi: {survey.title}</h2>
          <p className="text-xs text-slate-400">
            {responses.length} {responses.length === 1 ? 'odpowiedź' : responses.length < 5 ? 'odpowiedzi' : 'odpowiedzi'}
          </p>
        </div>
      </div>

      {responses.length === 0 ? (
        <div className="text-center py-16">
          <Inbox size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <h3 className="text-lg font-bold text-slate-500 dark:text-slate-400 mb-1">
            Brak odpowiedzi
          </h3>
          <p className="text-sm text-slate-400">
            Nikt jeszcze nie wypełnił tej ankiety. Wyślij link klientom.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {responses
            .sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime())
            .map((resp) => {
              const isExpanded = expandedId === resp.id;
              return (
                <div
                  key={resp.id}
                  className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden"
                >
                  {/* Header */}
                  <button
                    onClick={() => toggle(resp.id)}
                    className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition text-left"
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                      <User size={14} className="text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{resp.respondentName}</p>
                      <div className="flex items-center gap-3 text-[10px] text-slate-400">
                        <span className="flex items-center gap-0.5">
                          <Calendar size={9} /> {formatDate(resp.submittedAt)}
                        </span>
                        {resp.respondentEmail && (
                          <span className="flex items-center gap-0.5">
                            <Mail size={9} /> {resp.respondentEmail}
                          </span>
                        )}
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 animate-fade-in">
                      <div className="border-t border-slate-100 dark:border-slate-800 pt-4 space-y-3">
                        {survey.fields.map((field) => (
                          <div key={field.id} className="grid grid-cols-1 sm:grid-cols-3 gap-1">
                            <span className="text-xs font-medium text-slate-500 sm:text-right pr-3">{field.label}</span>
                            <div className="sm:col-span-2 text-sm">
                              {renderAnswer(field, resp.answers[field.id])}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                        {onAnalyze && (
                          <button
                            onClick={() => onAnalyze(resp.id)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-white bg-brand-600 hover:bg-brand-700 transition font-medium"
                          >
                            <Activity size={12} /> Analizuj odpowiedź
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (window.confirm(`Usunąć odpowiedź od "${resp.respondentName}"?`)) {
                              deleteResponse(resp.id);
                              if (expandedId === resp.id) setExpandedId(null);
                            }
                          }}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition font-medium"
                        >
                          <Trash2 size={12} /> Usuń odpowiedź
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
